<script setup lang="ts">
import type {
	BehaviorConfig,
	LocationDataConfig,
	StyleConfig,
	Suggestion
} from './types';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { CurrentPage } from '~/types/mixpanel';

import {
	Command,
	CommandList,
	CommandSeparator
} from '@/components/SearchSuggestRadixCommand';
import { cn } from '@/utils';
import { useFirstFieldSuggestionHandler } from '~/composables/searchSuggest/useFirstFieldSuggestionHandler';
import { useFirstFieldSuggest } from '~/composables/useFirstFieldSuggest';
import { useNewSearchSuggest } from '~/composables/useNewSearchSuggest';
import { useResearchsSuggest } from '~/composables/useResearchsSuggest';
import { useSearchSuggestNavigation } from '~/composables/useSearchSuggestNavigation';
import { SEARCH_SUGGEST } from '~/constants';

import HistoryGroupIcon from '~icons/material-symbols-light/history';

import ListingEmptyState from '../ListingEmptyState/EmptyState.vue';
import CodeGroup from './CodeGroup.vue';
import InsideLabelSuggestInput from './InsideLabelSuggestInput.vue';
import LoadingGroup from './LoadingGroup.vue';
import NewGroup from './NewGroup.vue';

interface FirstFieldSuggestProps {
	// Configurações de dados
	locationData?: LocationDataConfig;
	// Configurações de estilo
	styleConfig?: StyleConfig;
	// Configurações de comportamento
	behaviorConfig?: BehaviorConfig;
	// Props individuais mantidas para compatibilidade (deprecated)
	searchTerm?: string;
	isFetchingPropertyByCodeSearch?: boolean;
	// Compatibilidade com props antigas
	regions?: LocationDataConfig['regions'];
	cities?: LocationDataConfig['cities'];
	macroregions?: LocationDataConfig['macroregions'];
	currentPage?: CurrentPage;
}

const {
	isFetchingPropertyByCodeSearch = false,
	locationData = {},
	styleConfig = {},
	behaviorConfig = {
		enableAnimation: true,
		closeOnClickOutside: true,
		closeOnSelect: true,
		showCloseIcon: false
	},
	searchTerm: externalSearchTerm,
	regions,
	cities,
	macroregions,
	currentPage
} = defineProps<FirstFieldSuggestProps>();

const emit = defineEmits<{
	(e: 'select-code', value: Suggestion['searchParams']): void;
	(e: 'select-history', value: Suggestion, isEmptyState: boolean): void;
	(e: 'update:search-term', value: string): void;
	(e: 'close-modal'): void;
}>();

// v-model para a localização selecionada (região/cidade)
const selectedLocation = defineModel<SelectAddressEvent | null>(
	'selectedLocation',
	{
		default: null
	}
);

const isDropdownOpened = defineModel<boolean>('isDropdownOpened', {
	default: false
});

const selectedValue = ref<SuggestionGroup>();
const commandListEl = useTemplateRef<HTMLElement>('commandListEl');
const inputEl =
	useTemplateRef<InstanceType<typeof InsideLabelSuggestInput>>('inputEl');

// Merge props antigas com configs novas para compatibilidade
const mergedLocationData = computed<LocationDataConfig>(() => ({
	regions: locationData?.regions || regions,
	cities: locationData?.cities || cities,
	macroregions: locationData?.macroregions || macroregions
}));

// Criar props compatível para useNewSearchSuggest
const compatibleProps = computed(() => ({
	...mergedLocationData.value,
	...behaviorConfig,
	...styleConfig,
	isFetchingPropertyByCodeSearch
}));

const {
	// State
	searchTerm,
	modelValue,
	commandEl,
	hasSelectedValue,
	isNavigatingWithKeyboard,
	backupSearchTerm,
	isUpdatingFromWatch,
	// Computed
	searchByCodeOption,
	shouldShowEmptyState,
	locationAutocompleteData,
	loading,

	// Research suggestions
	filteredLastResearchs,
	removeResearchs,

	// Props pass-through for styling
	placeholders,

	// Functions
	clear,
	clearLocationData,
	handleKeyDown,
	getDisplayValue,
	handleApplySearchTerm,
	executeLocationAutocomplete,
	setInitialSelection: setInitialSelectionFn
} = useNewSearchSuggest({
	props: compatibleProps.value,
	emit: ((event: string, value?: any) => {
		if (event === 'select-code') {
			emit('select-code', value);
		} else if (event === 'update:search-term') {
			emit('update:search-term', value);
		} else if (event === 'close-modal') {
			emit('close-modal');
		}
	}) as any,
	isDropdownOpen: isDropdownOpened,
	providers: ['cities', 'macroregions']
});

// Usar composable específico do primeiro campo para filtragem
const { filteredSuggestions } = useFirstFieldSuggest({
	searchTerm,
	locationAutocompleteData,
	filteredLastResearchs,
	searchByCodeOption
});

// Navegação
const {
	handleSelectCode,
	isFetchingPropertyByCodeSearch: internalIsFetchingPropertyByCodeSearch
} = useSearchSuggestNavigation({ currentPage });

// Usar o isFetchingPropertyByCodeSearch do pai ou o interno
const isFetchingByCode = computed(
	() =>
		isFetchingPropertyByCodeSearch ||
		internalIsFetchingPropertyByCodeSearch.value
);

const isUserFocusedOpening = ref<boolean>(false);
const backupSelectedLocation = ref<SelectAddressEvent | null>(null);
const { updateResearchs } = useResearchsSuggest();

// Track if user is actively typing
const isTyping = ref(false);
const typingTimeout = ref<NodeJS.Timeout>();

// Show loading only when user has typed enough characters and is loading/typing
const isLoadingOrTyping = computed<boolean>(() => {
	const hasEnoughChars =
		searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH;
	return hasEnoughChars && (loading.value || isTyping.value);
});

// Google Maps API para obter coordenadas (alinha comportamento com SecondFieldSuggest)
const { $googleMapsApi } = useNuxtApp();

async function getLocation(placeId: string | null) {
	if (!placeId || !$googleMapsApi?.places) return { lat: null, lng: null };

	const { Place } = $googleMapsApi.places;

	const place = new Place({
		id: placeId
	});

	await place.fetchFields({
		fields: ['location']
	});

	return {
		lat: place.location.lat(),
		lng: place.location.lng()
	};
}

// Suggestion handler with strategy pattern
const { handleSuggestion } = useFirstFieldSuggestionHandler(getLocation);

// Placeholders dinâmicos
const placeholderClosed = 'Cidades e regiões';
const placeholderOpen = 'Cidades, regiões e código do imóvel';

watch(
	() => externalSearchTerm,
	value => {
		if (value && value.trim()) {
			isUpdatingFromWatch.value = true;
			handleApplySearchTerm();
		} else if (value === undefined || value === null) {
			isUpdatingFromWatch.value = true;
			searchTerm.value = '';
		}
	},
	{ immediate: true }
);

function shouldSkipWatchExecution(): boolean {
	if (isUpdatingFromWatch.value) {
		isUpdatingFromWatch.value = false;
		return !isUserFocusedOpening.value;
	}
	return false;
}

function handleDropdownOpening(): void {
	const currentTerm = externalSearchTerm || searchTerm.value;

	// Se não há termo ou já foi limpo pelo handleInputFocus, não fazer nada
	if (!currentTerm || hasSelectedValue.value === false) {
		isUserFocusedOpening.value = false;
		return;
	}

	// Só fazer backup e limpar se o usuário focou e há um termo válido
	if (isUserFocusedOpening.value && currentTerm) {
		backupSearchTerm.value = currentTerm;
		isUpdatingFromWatch.value = true;
		searchTerm.value = '';
	}

	isUserFocusedOpening.value = false;
}

function handleDropdownClosing(): void {
	const shouldRestoreBackup =
		backupSearchTerm.value && !hasSelectedValue.value && !searchTerm.value;

	if (shouldRestoreBackup) {
		isUpdatingFromWatch.value = true;
		searchTerm.value = backupSearchTerm.value;

		// Restaurar também a localização selecionada
		if (backupSelectedLocation.value) {
			selectedLocation.value = backupSelectedLocation.value;
			hasSelectedValue.value = true;
		}
	}

	backupSearchTerm.value = '';
	backupSelectedLocation.value = null;
}

function setUserFocusedOpening(value: boolean): void {
	isUserFocusedOpening.value = value;
}

function closeDropdown() {
	if (!modelValue.value && externalSearchTerm) {
		handleApplySearchTerm();
	}
	isDropdownOpened.value = false;
	if (!searchTerm.value && !modelValue.value) {
		placeholders.value = placeholderClosed;
	}

	if (behaviorConfig.closeOnSelect) {
		emit('close-modal');
	}
}

function setInitialSelection() {
	setInitialSelectionFn(selectedValue);
}

function handleInputFocus() {
	setUserFocusedOpening(true);
	openDropdown();

	// placeholders.value = placeholderOpen;

	if (isNavigatingWithKeyboard.value) return;

	if (
		!searchTerm.value ||
		searchTerm.value.length < SEARCH_SUGGEST.MIN_SEARCH_LENGTH
	) {
		clearLocationData();
	} else {
		executeLocationAutocomplete();
	}
}

/**
 * Handle model value update (selection)
 * Delegates to strategy-based handler for cleaner code
 */
async function handleModelValueUpdate(suggestion: Suggestion) {
	// Clear backups when selecting a new option
	backupSearchTerm.value = '';
	backupSelectedLocation.value = null;

	await handleSuggestion(suggestion, {
		searchTerm,
		closeOnSelect: behaviorConfig.closeOnSelect,
		onClose: closeDropdown,
		onUpdateResearch: updateResearchs,
		onEmitHistory: (sug, isEmpty) => emit('select-history', sug, isEmpty),
		onEmitCode: params => emit('select-code', params),
		onUpdateSelectedLocation: location => {
			selectedLocation.value = location;
		},
		onSetHasSelectedValue: value => {
			hasSelectedValue.value = value;
		},
		getDisplayValue,
		shouldShowEmptyState: shouldShowEmptyState.value
	});
}

function handleInputClear() {
	clear();
	selectedLocation.value = null;
	backupSearchTerm.value = '';
	backupSelectedLocation.value = null;
	focusInput();
	// placeholders.value = placeholderOpen;
	openDropdown();
	clearLocationData();
}

function handleMouseDown(event: MouseEvent) {
	if (!behaviorConfig.closeOnClickOutside) return;

	if (commandEl.value && !commandEl.value.contains(event.target as Node)) {
		closeDropdown();
	}
}

function handleEnter(
	_: KeyboardEvent,
	selectedElement: HTMLElement | undefined
) {
	// Don't allow submission if typing or loading
	if (shouldShowEmptyState.value && !isTyping.value) {
		clear();
		return;
	}

	selectedElement?.click();
	inputEl.value?.blur();
}

function openDropdown() {
	isDropdownOpened.value = true;
}

function handleCloseModal() {
	emit('close-modal');
}

function focusInput() {
	inputEl.value?.focus();
}

watch([searchTerm, locationAutocompleteData], () => {
	if (isUpdatingFromWatch.value) return;
	setInitialSelection();
});

watch(
	isDropdownOpened,
	(isOpen, wasOpen) => {
		if (shouldSkipWatchExecution()) return;

		const isOpening = isOpen && !wasOpen;
		const isClosing = !isOpen && wasOpen;

		if (isOpening) {
			handleDropdownOpening();
		} else if (isClosing) {
			handleDropdownClosing();
		}
	},
	{ flush: 'sync' }
);

watch(
	() => isFetchingByCode.value,
	isFetching => {
		if (!isFetching && behaviorConfig.closeOnSelect) {
			closeDropdown();
		}
	}
);

watch(locationAutocompleteData, newData => {
	// Só abre o dropdown automaticamente se:
	// 1. Há dados
	// 2. O dropdown está fechado
	// 3. O termo de busca é válido
	// 4. O usuário está focado no input OU não há termo de busca externo
	// 5. Não há valor selecionado
	// 6. Não estamos fechando intencionalmente (usuário clicou fora)
	const shouldAutoOpen =
		newData?.length &&
		!isDropdownOpened.value &&
		searchTerm.value.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH &&
		isUserFocusedOpening.value &&
		!hasSelectedValue.value;

	if (shouldAutoOpen) {
		isDropdownOpened.value = true;
	}
});

watch(searchTerm, newValue => {
	if (typingTimeout.value) clearTimeout(typingTimeout.value);

	if (newValue.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
		isTyping.value = true;
		typingTimeout.value = setTimeout(() => {
			isTyping.value = false;
		}, SEARCH_SUGGEST.TYPING_DEBOUNCE_MS);
	} else {
		isTyping.value = false;
	}
});

watch(hasSelectedValue, selected => {
	if (selected) {
		isTyping.value = false;
		if (typingTimeout.value) clearTimeout(typingTimeout.value);
	}
});

// Inicializar placeholder
onMounted(() => {
	if (!isDropdownOpened.value) {
		placeholders.value = placeholderClosed;
	}
});

// Cleanup typing timeout on unmount
onUnmounted(() => {
	if (typingTimeout.value) {
		clearTimeout(typingTimeout.value);
	}
});

onKeyStroke('Escape', e => {
	e.preventDefault();
	if (behaviorConfig.closeOnClickOutside) {
		closeDropdown();
	}
});

useEventListener(document, 'mousedown', handleMouseDown);
</script>

<template>
	<div ref="commandEl" class="relative">
		<Command
			v-model="modelValue"
			v-model:search-term="searchTerm"
			v-model:selected-value="selectedValue"
			:open="isDropdownOpened"
			:class="cn('h-auto w-full border-0', styleConfig.class)"
			:display-value="getDisplayValue"
			reset-search-term-on-blur
			reset-search-term-on-select
			@update:model-value="handleModelValueUpdate as any"
			@keydown="handleKeyDown"
		>
			<InsideLabelSuggestInput
				ref="inputEl"
				placeholder="Cidades e regiões"
				:open-input-placeholder="placeholderOpen"
				:wrapper-classes="styleConfig.inputWrapperClasses"
				:class="cn('text-ellipsis pl-4 text-primary', styleConfig.inputClasses)"
				:auto-focus="false"
				:input-clear-icon-wrapper-classes="
					styleConfig.inputClearIconWrapperClasses
				"
				:input-clear-icon-classes="styleConfig.inputClearIconClasses"
				:input-search-icon-wrapper-classes="
					styleConfig.inputSearchIconWrapperClasses
				"
				:input-search-icon-classes="styleConfig.inputSearchIconClasses"
				:model-value="searchTerm"
				:enable-animation="behaviorConfig.enableAnimation"
				:show-close-icon="behaviorConfig.showCloseIcon"
				@focus.prevent="handleInputFocus"
				@clear="handleInputClear"
				@close-modal="handleCloseModal"
				@enter="handleEnter"
			>
				<template #prefix>
					<slot name="input-prefix" />
				</template>
				<template #suffix>
					<div
						:class="
							cn(
								'flex h-full w-7 mr-3 items-center justify-end',
								styleConfig.inputClearIconWrapperClasses
							)
						"
					>
						<IconMaterialSymbolsLightCancel
							v-show="modelValue"
							:class="
								cn(
									'size-5 cursor-pointer text-white-dark-3',
									styleConfig.inputClearIconClasses
								)
							"
							@click="() => handleInputClear()"
						/>
					</div>
				</template>
			</InsideLabelSuggestInput>

			<CommandList
				v-if="isDropdownOpened"
				ref="commandListEl"
				:class="
					cn(
						'absolute top-full left-0 right-0 z-50 h-auto max-h-[400px] border border-white-dark-2 border-t-0 bg-white p-0',
						{
							'overflow-hidden': isLoadingOrTyping
						},
						styleConfig.listWrapperClasses
					)
				"
			>
				<div
					v-show="shouldShowEmptyState && !isTyping"
					:class="cn('px-5 py-6', styleConfig.emptyStateWrapperClasses)"
				>
					<ListingEmptyState
						:show-badges="false"
						:icon-wrapper-classes="
							cn('size-[47.5px]', styleConfig.emptyStateIconWrapperClasses)
						"
						:icon-classes="
							cn('size-[22.5px]', styleConfig.emptyStateIconClasses)
						"
						:title-classes="cn('text-lg', styleConfig.emptyStateTitleClasses)"
						:description-classes="
							cn('text-sm', styleConfig.emptyStateDescriptionClasses)
						"
					>
						<template #title>Nenhum resultado encontrado</template>
						<template #description>
							Tente uma busca diferente ou verifique a grafia
						</template>
					</ListingEmptyState>
				</div>

				<LoadingGroup
					v-for="index in 3"
					:key="`loading-group-${index}`"
					:group-heading-classes="styleConfig.groupHeadingClasses"
					:group-icon-classes="styleConfig.groupIconClasses"
					:group-item-wrapper-classes="styleConfig.groupItemWrapperClasses"
					:group-item-text-classes="styleConfig.groupItemTextClasses"
					:group-item-sub-text-classes="styleConfig.groupItemSubTextClasses"
					:active="isLoadingOrTyping"
				/>

				<template v-if="!isLoadingOrTyping && filteredSuggestions?.length">
					<template
						v-for="(suggestionGroup, index) in filteredSuggestions"
						:key="`suggestion-group-${suggestionGroup.key}`"
					>
						<CommandSeparator
							class="bg-white-dark-2"
							:class="{ hidden: !suggestionGroup.visible || index === 0 }"
						/>

						<!-- Grupo de histórico com opção de remover -->
						<NewGroup
							v-if="suggestionGroup.key === 'lastResearchs'"
							:suggestion-key="'lastResearchs'"
							:heading="suggestionGroup.heading"
							:suggestions="suggestionGroup.suggestions"
							:search-term="searchTerm"
							is-removable
							:class="styleConfig.groupWrapperClasses"
							:group-heading-classes="styleConfig.groupHeadingClasses"
							:group-icon-classes="styleConfig.groupIconClasses"
							:group-item-wrapper-classes="styleConfig.groupItemWrapperClasses"
							:group-item-text-classes="styleConfig.groupItemTextClasses"
							:group-item-sub-text-classes="styleConfig.groupItemSubTextClasses"
							@remove-suggestion="removeResearchs"
						/>

						<!-- Outros grupos de sugestões -->
						<NewGroup
							v-else-if="suggestionGroup.key !== 'code'"
							:suggestion-key="suggestionGroup.key"
							:heading="suggestionGroup.heading"
							:suggestions="suggestionGroup.suggestions"
							:search-term="searchTerm"
							:class="styleConfig.groupWrapperClasses"
							:group-heading-classes="styleConfig.groupHeadingClasses"
							:group-icon-classes="styleConfig.groupIconClasses"
							:group-item-wrapper-classes="styleConfig.groupItemWrapperClasses"
							:group-item-text-classes="styleConfig.groupItemTextClasses"
							:group-item-sub-text-classes="styleConfig.groupItemSubTextClasses"
						/>

						<CodeGroup
							v-else-if="suggestionGroup.key === 'code'"
							:search-term="searchTerm"
							suggestion-key="code"
							:suggestions="suggestionGroup.suggestions as any"
							:is-fetching-property-by-code-search="isFetchingByCode"
							:class="styleConfig.groupWrapperClasses"
							:group-heading-classes="styleConfig.groupHeadingClasses"
							:group-icon-classes="styleConfig.groupIconClasses"
							:group-item-wrapper-classes="styleConfig.groupItemWrapperClasses"
							:group-item-text-classes="styleConfig.groupItemTextClasses"
							:group-item-sub-text-classes="styleConfig.groupItemSubTextClasses"
						/>
					</template>
				</template>
			</CommandList>
		</Command>
	</div>
</template>
